import React, { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";

import { toLocalTimeFromUTCWithOffset, toLocalTimeFromUTC } from "datetime";
import { usePersistedState } from "hooks";
import firebase from "firebaseFactory";
import { Page } from "modules/layout/Page";
import { Button } from "components/Button";
import { HighchartsFromAthenaResults } from "components/highcharts/HighchartsFromAthenaResults";
import {
  FormButtonRow,
  Form,
  FormSingleFieldRow,
  FormSubmit,
  FormTextArea,
} from "components/Form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "components/Table";

interface FormData {
  query: string;
}

interface QueryHistoryItem {
  timestamp: number;
  query: string;
  totalExecutionTimeInMillis: number;
  dataScannedInBytes: number;
  url: string;
  headUrl: string;
  expiry: Date;
  totalSizeInKB?: number;
  totalRows?: number;
}

export const DataQueryPage = ({
  runArbitraryAthenaQuery = firebase.function("runArbitraryAthenaQuery"),
  getDateNow = () => Date.now(),
}) => {
  const maxQueryHistory = 20;
  const [isLoading, setLoading] = useState(false);
  const [status, setStatus] = useState<string>();
  const [viewingQueryIndex, setViewingQueryIndex] = useState<
    number | undefined
  >();
  const [highlightedQueryIndex, setHighlightedQueryIndex] = useState<number>(0);
  // these are persisted in local storage
  // right now assuming that this isn't 'sensitive'
  // as it won't get cleared when someone signs out
  // this includes the 'signed' urls to S3 for the results
  // TODO: move to firestore instead?
  const [persistedQueryHistory, setPersistedQueryHistory] = usePersistedState<
    Array<QueryHistoryItem>
  >("reports/queryHistory", []);

  const {
    register,
    handleSubmit,
    errors,
    setValue: setFormValue,
    watch: watchFormValue,
  } = useForm<FormData>({
    defaultValues: {
      query:
        "SELECT timestamp_utc,grid_current_a FROM pack_data WHERE dt=timestamp '2020-04-28' AND device_id='UG3' ORDER BY timestamp_utc LIMIT 1000",
    },
  });
  const query = watchFormValue("query");

  const onClickSubmit = handleSubmit(async ({ query }) => {
    setLoading(true);
    setStatus("Waiting for query...");
    const result = await runArbitraryAthenaQuery.action({ query });
    if (result.data.error) {
      setStatus(result.data.error);
      setLoading(false);
      return;
    }
    const historyItem = {
      timestamp: getDateNow(),
      query: query,
      url: result.data.url as string,
      headUrl: result.data.headUrl as string,
      expiry: result.data.urlExpiry,
      dataScannedInBytes: result.data.dataScannedInBytes,
      totalExecutionTimeInMillis: result.data.totalExecutionTimeInMillis,
    };
    setPersistedQueryHistory((prev) => [
      historyItem,
      ...prev.slice(0, maxQueryHistory),
    ]);
    setHighlightedQueryIndex(0);
    setViewingQueryIndex(0);
  });

  const onChartComplete = useCallback(
    ({
      url,
      totalRows,
      totalSizeInKB,
    }: {
      url: string;
      totalRows: number;
      totalSizeInKB: number;
    }) => {
      setPersistedQueryHistory((prev) => {
        if (
          viewingQueryIndex === undefined ||
          prev[viewingQueryIndex].url !== url
        ) {
          return prev;
        }
        const queryHistory = prev.slice();
        queryHistory[viewingQueryIndex].totalSizeInKB = totalSizeInKB;
        queryHistory[viewingQueryIndex].totalRows = totalRows;
        return queryHistory;
      });
      setLoading(false);
    },
    [viewingQueryIndex, setPersistedQueryHistory]
  );

  const onChartLoading = useCallback(() => {
    setLoading(true);
  }, []);

  useEffect(() => {
    if (persistedQueryHistory.length > highlightedQueryIndex) {
      setFormValue("query", persistedQueryHistory[highlightedQueryIndex].query);
    }
  }, [highlightedQueryIndex, persistedQueryHistory, setFormValue]);

  return (
    <Page status={status}>
      <Form fullWidth onSubmit={onClickSubmit}>
        <FormSingleFieldRow>
          <FormTextArea
            tall
            label="SQL"
            name="query"
            note={
              !query.match(/[^\w]dt[^\w]/i)
                ? "Warning: You do not have a constraint on 'dt' (delivery time), so *all* historical data will be scanned. You should add a condition on 'dt' even when also querying on 'timestamp_utc'."
                : ""
            }
            required
            placeholder="Athena SQL query"
            register={register}
            errors={errors}
          />
        </FormSingleFieldRow>
        <FormButtonRow>
          <FormSubmit label="Run query" isLoading={isLoading} />
        </FormButtonRow>
      </Form>
      <HighchartsFromAthenaResults
        athenaResults={
          viewingQueryIndex !== undefined
            ? persistedQueryHistory[viewingQueryIndex]
            : undefined
        }
        onStatusUpdate={setStatus}
        onLoading={onChartLoading}
        onComplete={onChartComplete}
      />
      <Table className="w-full">
        <TableHead>
          <TableRow>
            <TableHeaderCell>Date</TableHeaderCell>
            <TableHeaderCell>Query</TableHeaderCell>
            <TableHeaderCell>Size</TableHeaderCell>
            <TableHeaderCell nowrap>Execution Time</TableHeaderCell>
            <TableHeaderCell nowrap>Data Scanned</TableHeaderCell>
            <TableHeaderCell nowrap># Results</TableHeaderCell>
            <TableHeaderCell>Actions</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {persistedQueryHistory.map((item, index) => (
            <TableRow
              key={index}
              onClick={() => setHighlightedQueryIndex(index)}
              className={`${
                highlightedQueryIndex === index
                  ? "bg-gray-300"
                  : "hover:bg-gray-100"
              } cursor-pointer`}
            >
              <TableCell
                nowrap
                title={toLocalTimeFromUTCWithOffset(item.timestamp)}
              >
                {toLocalTimeFromUTC(item.timestamp)}
              </TableCell>
              <TableCell>{item.query}</TableCell>
              <TableCell nowrap>{item.totalSizeInKB?.toFixed(0)}KB</TableCell>
              <TableCell nowrap>
                {(item.totalExecutionTimeInMillis / 1000).toFixed(2)} seconds
              </TableCell>
              <TableCell nowrap>
                {(item.dataScannedInBytes / 1024).toFixed(0)} KB
              </TableCell>
              <TableCell nowrap>{item.totalRows}</TableCell>
              <TableCell nowrap>
                <div className="flex">
                  <a
                    className="w-8 whitespace-no-wrap cursor-pointer flex bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded focus:outline-none focus:shadow-outline"
                    title="Download CSV"
                    href={persistedQueryHistory[index].url}
                  >
                    <svg fill="white" className="h-5 w-5" viewBox="0 0 20 20">
                      <path d="M13,8 L13,2 L7,2 L7,8 L2,8 L10,16 L18,8 L13,8 Z M0,18 L20,18 L20,20 L0,20 L0,18 Z"></path>
                    </svg>
                  </a>
                  <Button
                    title="View Results"
                    className="ml-2"
                    compact
                    isLoading={isLoading}
                    onClick={() => setViewingQueryIndex(index)}
                  >
                    <svg fill="white" className="h-5 w-5" viewBox="0 0 20 20">
                      <g
                        stroke="none"
                        strokeWidth="1"
                        fill="none"
                        fillRule="evenodd"
                      >
                        <g fill="white">
                          <path
                            d="M19.8005808,10 C17.9798698,6.43832409 14.2746855,4 10,4 C5.72531453,4 2.02013017,6.43832409 0.199419187,10 C2.02013017,13.5616759 5.72531453,16 10,16 C14.2746855,16 17.9798698,13.5616759 19.8005808,10 Z M10,14 C12.209139,14 14,12.209139 14,10 C14,7.790861 12.209139,6 10,6 C7.790861,6 6,7.790861 6,10 C6,12.209139 7.790861,14 10,14 Z M10,12 C11.1045695,12 12,11.1045695 12,10 C12,8.8954305 11.1045695,8 10,8 C8.8954305,8 8,8.8954305 8,10 C8,11.1045695 8.8954305,12 10,12 Z"
                            id="Combined-Shape"
                          ></path>
                        </g>
                      </g>
                    </svg>
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Page>
  );
};
